import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import client from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await client.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
            setSent(true);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't send reset email");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-background">
            <div className="w-full max-w-sm" data-testid="forgot-password-page">
                <Link to="/" data-testid="brand-link" className="inline-flex items-center gap-2 mb-10">
                    <Logo className="h-14 w-auto" />
                </Link>

                {sent ? (
                    <div data-testid="forgot-password-sent">
                        <div className="overline mb-3">// check your inbox</div>
                        <h1 className="font-display text-3xl tracking-tight mb-4">Link on its way.</h1>
                        <p className="text-sm text-muted-foreground leading-relaxed mb-8">
                            If an account exists for <span className="font-mono text-foreground">{email}</span>,
                            we just sent a reset link there. It expires in 60 minutes and can only be used once.
                        </p>
                        <p className="text-xs text-muted-foreground mb-8">
                            Didn't get it? Check spam, or wait a minute and try again.
                        </p>
                        <Link
                            to="/login"
                            data-testid="back-to-login-link"
                            className="text-primary hover:underline text-sm"
                        >
                            ← Back to sign in
                        </Link>
                    </div>
                ) : (
                    <form onSubmit={submit} data-testid="forgot-password-form">
                        <div className="overline mb-3">// reset password</div>
                        <h1 className="font-display text-3xl tracking-tight mb-3">Forgot it? Happens.</h1>
                        <p className="text-sm text-muted-foreground mb-8">
                            Type the email tied to your InPhase account. We'll send you a one-shot link
                            to pick a new password.
                        </p>

                        <label className="text-xs text-muted-foreground block mb-2">Email</label>
                        <input
                            data-testid="forgot-password-email-input"
                            type="email"
                            required
                            autoFocus
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-input border border-border px-3 py-3 font-mono text-sm focus:outline-none focus:border-primary"
                            placeholder="you@company.com"
                        />

                        <button
                            data-testid="forgot-password-submit-btn"
                            disabled={loading}
                            className="w-full mt-8 bg-primary text-primary-foreground py-3 font-medium hover:bg-primary/90 disabled:opacity-50"
                        >
                            {loading ? "Sending…" : "Send reset link →"}
                        </button>

                        <div className="mt-6 text-xs text-muted-foreground">
                            Remembered it? <Link to="/login" data-testid="switch-login-link" className="text-primary hover:underline">Back to sign in</Link>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}
